import {
  computePosition,
  flip,
  shift,
  offset,
  autoUpdate,
} from '@floating-ui/dom';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import useClickOutside from 'lib/hooks/useClickOutside';
import style from './base-card.module.scss';

export interface BaseCardProps {
  contentClassName?: string;
  triggerClassName?: string;
  renderTrigger: (active: boolean, toggle: () => void) => React.ReactNode;
  content: React.ReactNode;
}

// Finds the closest ancestor with data-portal-container attribute,
// or falls back to document.body
const getPortalContainer = (element: HTMLElement | null): HTMLElement => {
  if (!element) return document.body;

  const container = element.closest('[data-portal-container]');
  if (container instanceof HTMLElement) {
    return container;
  }

  return document.body;
};

export const BaseCard: React.FC<BaseCardProps> = ({
  contentClassName = '',
  triggerClassName = '',
  renderTrigger,
  content,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement>(
    document.body
  );

  useLayoutEffect(() => {
    if (triggerRef.current) {
      setPortalContainer(getPortalContainer(triggerRef.current));
    }
  }, []);

  const toggleOpen = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  // Handle popup positioning and auto-updates
  // Uses floating-ui to:
  // - Position popup below its trigger with 8px offset
  // - Flip position if not enough space below
  // - Shift to prevent viewport overflow
  // - Auto-update position on scroll/resize
  useEffect(() => {
    if (isOpen && triggerRef.current && contentRef.current) {
      cleanupRef.current = autoUpdate(
        triggerRef.current,
        contentRef.current,
        () => {
          if (triggerRef.current && contentRef.current) {
            computePosition(triggerRef.current!, contentRef.current!, {
              placement: 'bottom',
              middleware: [offset(8), flip(), shift({ padding: 8 })],
            }).then(({ x, y }) => {
              if (contentRef.current) {
                Object.assign(contentRef.current.style, {
                  left: `${x}px`,
                  top: `${y}px`,
                });
              }
            });
          }
        }
      );
    }

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [isOpen]);

  useClickOutside({
    refs: [triggerRef, contentRef],
    onClickOutside: () => {
      if (isOpen) {
        setIsOpen(false);
      }
    },
  });

  return (
    <>
      <div
        ref={triggerRef}
        className={classNames(triggerClassName, {
          [style.triggerActive]: isOpen,
        })}
        onClick={toggleOpen}
      >
        {renderTrigger(isOpen, toggleOpen)}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={contentRef}
            data-portal-container
            className={classNames(style.cardContent, contentClassName)}
            style={{
              position: 'absolute',
              zIndex: 1000,
              maxHeight: 'none',
              overflow: 'visible',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {content}
          </div>,
          portalContainer
        )}
    </>
  );
};
